// The user's home timezone, supplied app-wide — the single clock the whole UI
// renders in (see docs/timezones.md). Components read `useTz()` and pass the zone
// into the pure, zone-aware helpers in `dates.ts` and planning's `ui/lib/events.ts`.
//
// The value comes only from the `displayTimezone` setting. The single last-resort
// fallback is the browser's OWN resolved zone (a runtime truth) — never a
// hardcoded city. This is the one place in the client that fallback lives.

import { createContext, useContext, type ReactNode } from "react";
import { resolveHostZone } from "@/platform/kit";
import { useDisplayTimezone } from "@/platform/settings/client";

const TimezoneContext = createContext<string>(resolveHostZone());
const SettledContext = createContext<boolean>(false);

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const { data, isFetched } = useDisplayTimezone();
  const tz = data?.timezone ?? resolveHostZone();
  return (
    <TimezoneContext.Provider value={tz}>
      <SettledContext.Provider value={isFetched}>{children}</SettledContext.Provider>
    </TimezoneContext.Provider>
  );
}

/** The user's home timezone (IANA name) — the app's single clock. */
export function useTz(): string {
  return useContext(TimezoneContext);
}

/** Whether `useTz` is the setting yet, rather than the browser's own zone
 *  standing in while it loads. Around midnight the two name different days. */
export function useTzSettled(): boolean {
  return useContext(SettledContext);
}
